import { Component, OnInit, OnDestroy, Input } from '@angular/core';
import { TransactionDto } from '../../../dto/transactionDto';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { TransactionsService } from '@services/entity/transactions.service';
import { Subscription, interval } from 'rxjs';
import { RemoteStartStopDialogContentComponent } from '@components/dialog/remote-start-stop-dialog-content/remote-start-stop-dialog-content.component';
import { ChargingPowerProfile } from '@models/chargingPowerProfile';
import { AuthenticationService } from '@services/authentication/authentication.service';
import { ActivatedRoute } from '@angular/router';
import { RunningTransactionDetails } from '@dto/runningTransactionDetails';
import * as _ from 'lodash-es';
import { ChargingPointStatusEnum } from '@enums/chargingPointStatus.enum';

@Component({
  selector: 'app-dashboard-running-transactions',
  templateUrl: './dashboard-running-transactions.component.html',
  styleUrls: ['./dashboard-running-transactions.component.scss']
})
export class DashboardRunningTransactionsComponent implements OnInit, OnDestroy {

  @Input() initialTransactions: TransactionDto[];
  public entities: TransactionDto[];
  public selectedTransaction: TransactionDto;
  private refreshEntity: Subscription;
  public isViewingSelectedTransaction: boolean = false;
  public isOperator: boolean;
  public force: boolean;
  public transactionTotalPower: number = 0;
  public loading: boolean = true
  public sortRunningTransaction: string;
  public sortDirection: string;

  constructor(
    private authenticationService: AuthenticationService,
    private activatedRoute: ActivatedRoute,
    private transactionsService: TransactionsService,
    private dialog: NgbModal) {  }


  ngOnInit(): void {
    if (this.authenticationService.userHasRole('Operator')) this.isOperator = true;
    this.entities = this.initialTransactions

    this.updateEntities();
    this.setRefreshEntities();
  }

  ngOnDestroy(): void {
    this.refreshEntity.unsubscribe();
  }

  calcDiff(starttime: string) {
    var startTime = new Date(starttime);
    var difference = Date.now() - (startTime.getTime()); // This will give difference in milliseconds
    return this.humanReadableDuration(difference);
  }

  sortEntities(event) {
    if (event) {
      this.sortRunningTransaction = event.target.value;
    }
    if (this.sortRunningTransaction = "status") { 
      if (this.sortDirection != 'asc') {
        this.entities.sort((a, b) => a.status.id > b.status.id ? 1 : -1);
        this.sortDirection = 'asc';
      } else {
        this.entities.sort((a, b) => a.status.id > b.status.id ? 1 : -1).reverse();
        this.sortDirection = "desc"
      }
    } else {
      if (this.sortDirection != 'asc') {
        this.entities.sort((a, b) => a.startTimestamp < b.startTimestamp ? 1 : -1);
        this.sortDirection = 'asc';
      } else {
        this.entities.sort((a, b) => a.startTimestamp > b.startTimestamp ? 1 : -1).reverse();
        this.sortDirection = "desc"
      }
    }
    //}
  }

  updateEntities() {
    if(this.isOperator) {
      this.transactionsService.getRunningTransactionsDetailOfCustomer(this.activatedRoute.parent.snapshot.params.key).subscribe(result => {
        this.mapDetails(result);
      }, error => {});
    } else {
      this.transactionsService.getRunningTransactionsDetail().subscribe(result => {
        this.mapDetails(result);
      }, error => {});
    }
  }

  trackBy(field: string): (_index: number, item: any) => string | number {
    return (_index: number, item: any) => {
      if (!item) {
        return null;
      }
      return item[field] as (string | number);
    };
  }

  setRefreshEntities() {
    this.refreshEntity = interval(30000).subscribe(async (val) => {
      this.updateEntities();
    })
  }

  humanReadableDuration(msDuration: number): string {
    const h = Math.floor(msDuration / 1000 / 60 / 60);
    const m = Math.floor((msDuration / 1000 / 60 / 60 - h) * 60);
    const s = Math.floor(((msDuration / 1000 / 60 / 60 - h) * 60 - m) * 60);

    const seconds: string = s < 10 ? `0${s}` : `${s}`;
    const minutes: string = m < 10 ? `0${m}` : `${m}`;
    const hours: string = h < 10 ? `0${h}` : `${h}`;

    if (hours == "00" || hours == "0") {
      return `${minutes} Minuten`;
    }
    if (hours == "01") {
      return `${hours} Stunde ${minutes} Minuten`;
    }
    return `${hours} Stunden ${minutes} Minuten`;
  }

  mapDetails(details: RunningTransactionDetails[]) {
    this.transactionTotalPower = 0;
    
    for (let entity of this.entities) {
      entity.duration = this.calcDiff(entity.startTimestamp.toString());
      
      const detail = details.find(d => d.id == entity.id);
      // only show charging point status, if charging station itself is ok
      entity.status = detail.chargingStationStatus.id == ChargingPointStatusEnum.Available ? detail.chargingPointStatus : detail.chargingStationStatus;
      entity.chargingPowerProfile = detail.chargingPowerProfile ? detail.chargingPowerProfile : new ChargingPowerProfile({ id: 1, name: 'Standard', key: 'now' });
      entity.currentMeter = detail.currentMeterValue;
      entity.currentPower = detail.currentPower;
      entity.currentSoC = detail.currentSoC;
      this.transactionTotalPower += Number.parseInt(detail.currentPower); //unit unknown
    }

    this.loading = false;
  }

  selectTransaction(id) {
    this.selectedTransaction = this.entities.find(t => t.id == id)
    if (this.selectedTransaction.currentMeter == null) {
      this.selectedTransaction.meterAmount = 0;
    } else {
      this.selectedTransaction.meterAmount = this.selectedTransaction.currentMeter - this.selectedTransaction.meterStart;
    }
    this.isViewingSelectedTransaction = true
  }

  closePopup() {
    this.isViewingSelectedTransaction = false
  }

  openRemoteStopDialog(transaction: TransactionDto, transactionId) {
    this.selectedTransaction = this.entities.find(t => t.id == transactionId)
    if (this.force) {
      this.transactionsService.closeTransaction(this.selectedTransaction.id).subscribe(res => {
        if (res) {
          this.updateEntities();
        }
      }, error => {  })
    } else {
      const dialogRef = this.dialog.open(RemoteStartStopDialogContentComponent, { centered: true, backdrop: 'static' });
      dialogRef.componentInstance.transaction = transaction;
    }
  }

  getChargingPointPosition(transaction: TransactionDto) {
    if (!transaction) return ''
    if (!transaction.chargingStationNumberOfChargingPoints) return ''

    if (transaction.chargingStationNumberOfChargingPoints > 1) {
      return `(Ladepunkt ${transaction.connectorId})`
    }

    return ''
  }

  matches(entity: TransactionDto, term: string) {
    return TransactionsService.matches(entity, term);
  }
}
